import React, { ReactNode } from "react";
import { css, styled } from "styled-components";
import { Body1, Body2, H2, H3, Subtitle1 } from "melodies-source/Text";
import { Card as MelodiesCard } from "melodies-source/Card";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { useContactContext } from "Components/ContactContext";
import { getLoginUrl } from "auth";
import { ReactComponent as SvgSetLogo } from "assets/svg/logo.svg";

interface HeadingProps {
  icon: ReactNode;
  text: string;
}

interface ActionsProps {
  buttons?: ReactNode;
  link?: "login" | "contact";
  custom?: ReactNode;
}

interface Props {
  children: ReactNode;
  size?: "compact" | "small" | "large";
  disableCardHeader?: boolean;
  heading?: HeadingProps;
  actions?: ActionsProps;
  cardLogo?: React.ReactNode;
}

export type CardProps = Props;

export const Card: React.FC<Props> = ({
  children,
  size = "small",
  disableCardHeader = false,
  heading,
  actions,
  cardLogo,
}) => {
  const isMobile = useMobileMediaQuery();
  const { setShowContact } = useContactContext();
  const Heading = isMobile ? Subtitle1 : H3;
  const BodyText = isMobile ? Body2 : Body1;

  let actionLink;

  if (actions) {
    switch (actions.link) {
      case "login":
        actionLink = (
          <Body1>
            Already have an account? <a href={getLoginUrl()}>Log in</a>
          </Body1>
        );
        break;
      case "contact":
        actionLink = (
          <BodyText>
            Questions or issues?{" "}
            <a href={`#contact`} onClick={() => setShowContact(true)}>
              Contact MAX
            </a>
          </BodyText>
        );
        break;
      default:
        actionLink = isMobile ? null : <div />;
    }
  }

  return (
    <StyledCard size={size} isElevated>
      {!(disableCardHeader || size === "compact") && (
        <Header>
          <SvgSetLogo />
          <Heading>Create Your Account</Heading>
        </Header>
      )}
      {cardLogo}
      <CardContent size={size}>
        {heading && (
          <CardHeading>
            {heading.icon}
            <H2>{heading.text}</H2>
          </CardHeading>
        )}
        {children}
      </CardContent>
      {actions && (
        <Actions>
          {actions.custom || actionLink}
          {actions.buttons && <Buttons>{actions.buttons}</Buttons>}
        </Actions>
      )}
    </StyledCard>
  );
};

const Header = styled.div`
  background-color: #f7fafc;
  border-bottom: 1px solid #ebebeb;
  border-radius: 12px 12px 0 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 0 24px;
  height: 90px;

  ${H3}, ${Subtitle1} {
    color: #666;
  }

  ${({ theme }) => theme.media.mobile} {
    padding: 0 20px;
    height: 70px;

    svg {
      width: 63px;
      height: 32px;
    }
  }
`;

type StyledCardProps = Pick<Props, "size">;

const StyledCard = styled(MelodiesCard)<StyledCardProps>`
  box-sizing: content-box;
  display: flex;
  flex-direction: column;
  background: white;
  background-clip: padding-box;
  color: #333333;
  max-width: ${({ size }) => (size === "large" ? "900px" : "548px")};
  padding: 0;
  border: 8px solid rgba(255, 255, 255, 0.5);
  border-radius: 20px;
  position: relative;
  width: 100%;

  ${(p) =>
    p.size !== "compact" &&
    css`
      min-height: 600px;
    `};

  a {
    color: #00e;
  }

  ${({ theme }) => theme.media.mobile} {
    max-width: 480px;
    min-height: initial;
  }
`;

export const CardContent = styled.div<StyledCardProps>`
  padding: 24px 44px 32px;
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  ${(p) =>
    p.size === "compact" &&
    css`
      padding: 32px 44px;
    `};

  ${({ theme }) => theme.media.mobile} {
    padding: 20px;
    ${(p) =>
      p.size === "compact" &&
      css`
        button {
          width: 100%;
        }
      `};
  }
`;

const CardHeading = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  gap: 10px;

  ${H2} {
    color: #1b0076;
  }

  ${({ theme }) => theme.media.mobile} {
    gap: 5px;
    margin-bottom: 20px;

    ${H2} {
      font-size: 20px;
      line-height: 28px;
    }

    svg {
      width: 34px;
      height: 34px;
    }
  }
`;

const Actions = styled.div`
  padding: 0 24px 24px;
  padding-top: 5px !important;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;

  ${({ theme }) => theme.media.mobile} {
    padding: 0 20px 20px;
    flex-direction: column-reverse;
    align-items: center;
    gap: 20px;
    text-align: center;
  }
`;

const Buttons = styled.div`
  display: flex;
  gap: 20px;

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column-reverse;
    gap: 10px;
    width: 100%;
  }
`;
